<script setup lang="ts">
import { throttle } from 'throttle-debounce'
import type { PrimitivesValue } from '../../types/shared'
import type { TabsType, TabsColor, TabsSize } from './types'
import { APP_TABS_KEY } from './constants'

type Props = {
  modelValue?: PrimitivesValue
  type?: TabsType
  color?: TabsColor
  size?: TabsSize
  block?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'button',
  color: 'lighter',
  size: 'lg',
  block: true,
})

const emit = defineEmits(['update:model-value'])

const selectedValue = ref<PrimitivesValue>(props.modelValue)

const tabsElement = ref<HTMLElement | null>(null)

const resizeObserver = ref<ResizeObserver | null>(null)

const sliderStyles = ref({
  width: '100%',
  transform: 'translateX(0px)',
  opacity: 0,
})

const componentClasses = computed(() => {
  return [
    'app-tabs',
    `app-tabs--type-${props.type}`,
    {
      'app-tabs--block': props.block,
      [`app-tabs--color-${props.color}`]: props.type === 'button',
      [`app-tabs--size-${props.size}`]: props.type === 'button',
    },
  ]
})

const getSliderStyles = () => {
  const selectedTab = tabsElement?.value?.querySelector(
    '.app-tab--selected',
  ) as HTMLElement

  if (!selectedTab) return

  const { clientWidth, offsetLeft } = selectedTab

  sliderStyles.value.width = `${clientWidth}px`
  sliderStyles.value.transform = `translateX(${offsetLeft}px)`
  sliderStyles.value.opacity = 1
}

const updateValue = (newValue: PrimitivesValue) => {
  selectedValue.value = newValue

  emit('update:model-value', newValue)
}

const attachObserver = () => {
  resizeObserver.value = new ResizeObserver(throttle(500, getSliderStyles))

  if (resizeObserver.value && tabsElement.value) {
    resizeObserver.value.observe(tabsElement.value)
  }
}

const detachObserver = () => {
  if (resizeObserver.value && tabsElement.value) {
    resizeObserver.value.unobserve(tabsElement.value)
  }
}

watch(
  () => props.modelValue,
  (updatedValue) => (selectedValue.value = updatedValue),
)

onMounted(() => {
  getSliderStyles()
  attachObserver()
})

onBeforeUnmount(() => detachObserver())

onUpdated(() => getSliderStyles())

provide(APP_TABS_KEY, { selectedValue, updateValue })
</script>

<template>
  <div
    v-bind="$attrs"
    ref="tabsElement"
    :class="componentClasses"
    role="tablist"
  >
    <div class="app-tabs__content">
      <div class="app-tabs__slider" :style="sliderStyles" />
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-tabs {
  max-width: 100%;
  overflow: auto hidden;
  @include invisible-scroll-bar();

  &__content {
    position: relative;
    display: flex;
    border-radius: inherit;
  }

  &__slider {
    position: absolute;
    pointer-events: none;
    opacity: 0;
    transition-delay: 0s, 0s, 0.2s;
    transition-timing-function: cubic-bezier(0.25, 0.8, 0.5, 1);
    transition-duration: 0.2s;
    transition-property: width, transform, opacity;
  }

  &--block {
    width: 100%;
  }

  &--type-button {
    border-radius: 100px;

    :deep(.app-tab) {
      position: relative;
      flex: 0 1 auto;
      width: 100%;
      border-radius: 100px;

      @include media('>=desktop-mini') {
        &:focus-visible {
          font-weight: 800;
          outline: 2px solid var(--ui-text-primary);
          outline-offset: -2px;
        }
      }
    }

    :deep(.app-tab--selected) {
      font-weight: 800;
      pointer-events: none;
    }

    .app-tabs__slider {
      top: 0;
      bottom: 0;
      background-color: var(--ui-color-white);
      border: 1px solid;
      border-radius: 100px;
    }
  }

  &--type-text {
    position: relative;
    height: 44px;

    &::before {
      position: absolute;
      right: 0;
      bottom: 1px;
      left: 0;
      height: 1px;
      content: '';
      background-color: var(--ui-color-sandy-200);
    }

    :deep(.app-tab) {
      flex-grow: 1;
      align-items: flex-start;
      min-width: auto;
      height: 44px;
      padding: 12px;
      font-size: 16px;
      line-height: 20px;

      @include media('>=desktop-mini') {
        &:hover {
          color: var(--ui-color-primary);
        }
      }
    }

    :deep(.app-tab--selected) {
      color: var(--ui-color-primary);
    }

    .app-tabs__slider {
      bottom: 1px;
      height: 2px;
      background-color: var(--ui-color-primary) !important;
    }
  }

  &--color-lighter {
    background-color: var(--ui-color-sandy-50);

    .app-tabs__slider {
      border-color: var(--ui-color-sandy-100);
    }

    :deep(.app-tab) {
      @include media('>=desktop-mini') {
        &:hover {
          background-color: var(--ui-color-sandy-100);
        }
      }
    }
  }

  &--color-darker {
    background-color: var(--ui-color-sandy-100);

    .app-tabs__slider {
      border-color: var(--ui-color-sandy-200);
    }

    :deep(.app-tab) {
      @include media('>=desktop-mini') {
        &:hover {
          background-color: var(--ui-color-sandy-200);
        }
      }
    }
  }

  &--size-sm {
    height: 28px;

    :deep(.app-tab) {
      height: 28px;
      padding: 0 12px;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &--size-lg {
    height: 42px;
    padding: 4px;

    :deep(.app-tab) {
      height: 34px;
      padding: 0 16px;
      font-size: 14px;
      line-height: 18px;
    }
  }
}
</style>
